import React, { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getUserFilteredLocations } from '../../queries/locations';
import Cookies from 'js-cookie';
import { getCompanySession } from '../../queries/company';
import {
  setUser,
  setCompanyLoading,
  setCompanySuccess,
  setCompanyError,
  setLocationsLoading,
  setLocationsSuccess,
  setLocationsError,
  selectCompany,
  selectUser,
  selectLocations,
  selectSelectedLocationId,
  selectUserCanAccessAllLocations,
  setSelectedLocationId,
  resetLocationSelection,
  isUserAdminForCompany,
  readStoredLocationId,
} from '../../redux/locations';

/**
 * Compares two arrays by their `id` property to detect changes.
 * Returns true if the sets of IDs differ (different sizes or missing IDs).
 * @param {Array} arr1 - First array of objects with `id` property
 * @param {Array} arr2 - Second array of objects with `id` property
 * @returns {boolean} - True if the ID sets are different
 */
const haveIdsChanged = (arr1, arr2) => {
  const set1 = new Set((arr1 || []).map((item) => item.id));
  const set2 = new Set((arr2 || []).map((item) => item.id));
  
  return set1.size !== set2.size || [...set1].some((id) => !set2.has(id));
};

const LocationProvider = ({ children }) => {
  const router = useRouter();
  const { slug } = router.query;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  
  // Track previous company ID to detect company changes
  const prevCompanyIdRef = useRef(null);

  // Get user from main Redux store
  const mainUser = useSelector((state) => state.user?.user);
  const mainCompany = useSelector((state) => state.user?.company);

  // Get user and company from locations store
  const company = useSelector(selectCompany);
  const user = useSelector(selectUser);
  const locations = useSelector(selectLocations);
  const selectedLocationId = useSelector(selectSelectedLocationId);
  const canAccessAllLocations = useSelector(selectUserCanAccessAllLocations);

  // Set user data in locations store when main user changes
  // Must sync when locations change too, not just user ID, to keep query and auto-selection consistent
  // Also sync when access_role_instances change to detect admin status updates
  useEffect(() => {
    if (!mainUser) return;
    
    // Detect if user's assigned locations changed
    const locationsChanged = haveIdsChanged(
      mainUser.locations,
      user?.locations
    );
    
    // Detect if user's role instances changed (for admin status updates)
    const rolesChanged = haveIdsChanged(
      mainUser.access_role_instances,
      user?.access_role_instances
    );
    
    // Sync if no user, different user ID, location IDs changed, or role instances changed
    const shouldSync = !user || user.id !== mainUser.id || locationsChanged || rolesChanged;
    
    if (shouldSync) {
      dispatch(setUser(mainUser));
    }
  }, [mainUser, user, dispatch]);

  // Handle company change: reset locations, invalidate cache, and update company
  useEffect(() => {
    if (mainCompany && (!company || company.id !== mainCompany.id)) {
      // Use persisted company.id as fallback when prevCompanyIdRef.current is null (first render)
      // This ensures the reset path runs when persisted selectedLocationId belongs to a different company
      const previousCompanyId = prevCompanyIdRef.current ?? company?.id;
      const isCompanySwitch = previousCompanyId !== null && 
                              previousCompanyId !== undefined &&
                              previousCompanyId !== mainCompany.id;
      
      if (isCompanySwitch) {
        console.log('Company switch detected:', previousCompanyId, '->', mainCompany.id);
        
        // Reset location selection so auto-selection can run for the new company
        dispatch(resetLocationSelection());
        
        // Set loading state while new locations load
        dispatch(setLocationsLoading(true));
        
        // Remove old company's cached locations to force fresh fetch
        queryClient.removeQueries({ 
          queryKey: ['locations', previousCompanyId],
          exact: false 
        });
      }
      
      // Update company in locations store
      dispatch(setCompanySuccess(mainCompany));
      
      // Track the new company ID for next comparison
      prevCompanyIdRef.current = mainCompany.id;
    }
  }, [mainCompany, company, dispatch, queryClient]);

  // Fetch company data from API if needed
  // Same authenticated session entry withAuth hydrates from (so no second
  // request). It needs a JWT: this provider wraps every page, and pre-auth
  // pages (login, create-account) have a slug but no session to open.
  const { isLoading: companyQueryLoading } = useQuery(
    ['company-session', slug],
    () => getCompanySession(slug),
    {
      enabled: !!slug && !company && !!Cookies.get('jwt'),
      staleTime: 1000 * 60 * 5, // 5 minutes - prevents refetching if data is fresh
      cacheTime: 1000 * 60 * 10, // 10 minutes - keeps data in cache
      onSuccess: (data) => {
        if (!data?.id) return;
        const { moduleCatalog, ...sessionCompany } = data;
        dispatch(setCompanySuccess(sessionCompany));
      },
      onError: (error) => {
        dispatch(setCompanyError(error?.message));
      },
    }
  );

  // Update Redux loading state for company
  useEffect(() => {
    dispatch(setCompanyLoading(companyQueryLoading));
  }, [companyQueryLoading, dispatch]);

  // Fetch user-filtered locations
  const { isLoading: locationsQueryLoading, isFetching: locationsQueryFetching } = useQuery(
    ['locations', company?.id, user?.id],
    () => getUserFilteredLocations(company, user),
    {
      enabled: !!company?.id && !!user?.id,
      staleTime: 0, // Always consider stale to ensure fresh data on company switch
      cacheTime: 1000 * 60 * 10, // 10 minutes - keeps data in cache
      onSuccess: (data) => {
        dispatch(setLocationsSuccess(data?.data || []));
      },
      onError: (error) => {
        dispatch(setLocationsError(error?.message));
      },
    }
  );

  // Update Redux loading state for locations
  // Use isFetching instead of isLoading to catch refetches after company switch
  useEffect(() => {
    dispatch(setLocationsLoading(locationsQueryLoading || locationsQueryFetching));
  }, [locationsQueryLoading, locationsQueryFetching, dispatch]);

  // Auto-select location when locations load and nothing is selected
  // This is the canonical auto-selection logic - it filters user locations by company
  // to correctly handle users with locations across multiple companies
  useEffect(() => {
    // Bail out if company is not yet synced with mainCompany (switch in-flight)
    // This prevents auto-selecting a location from stale data during company transitions
    const isCompanySynced = company?.id === mainCompany?.id;

    // Only run if we have locations, no selection, and company is in sync.
    // Deliberately NOT gated on `!locationsQueryFetching` here: a stored id
    // that already resolves against the current (possibly still-refreshing)
    // `locations` array is safe to apply immediately — see below for the
    // case where it doesn't resolve.
    if (
      locations.length > 0 &&
      !selectedLocationId &&
      company &&
      mainUser &&
      isCompanySynced
    ) {
      // A stored selection wins over auto-selection, but only if it still resolves:
      // locations get deactivated and roles get revoked, and a dangling id would
      // scope every query to a location the API will reject.
      const stored = readStoredLocationId(company?.id);
      const storedIsValid =
        stored === 'all'
          ? canAccessAllLocations
          : locations.some((loc) => loc.id?.toString() === stored);
      if (stored && storedIsValid) {
        dispatch(setSelectedLocationId(stored));
        return;
      }

      // Nothing resolved. If the locations fetch is still in flight, `locations`
      // may be a partial/stale snapshot (react-query can return an
      // intermediate result ahead of a fresher one) — a stored id that fails
      // to resolve against it is not evidence the id is invalid. Leave
      // `selectedLocationId` unset (don't dispatch anything, don't touch
      // storage) so this effect re-runs the next time `locations` or
      // `locationsQueryFetching` changes, giving a stored id another chance
      // to resolve once the list settles.
      if (locationsQueryFetching) {
        return;
      }

      // The fetch has settled, so this `locations` array is the real,
      // complete list. Either there was no stored id, or there was one and
      // it genuinely does not resolve (location deleted, access revoked) —
      // fall through to auto-selection. This is the terminal path: it
      // guarantees the app always lands on a usable selection instead of
      // waiting forever on a stored id that will never resolve again.
      console.log('Auto-selecting location for company:', company.name, 'locations:', locations.length);

      // Check if user is admin for this company using centralized utility
      const isAdmin = isUserAdminForCompany(mainUser, company);
      
      // Filter user's locations to only those belonging to the current company
      // mainUser.locations spans ALL companies, but we need company-specific filtering
      const userLocations = (mainUser.locations || []).filter(
        (l) => l.company?.id === company.id
      );
      
      let newLocationId = '';
      
      if (isAdmin || userLocations.length === 0) {
        // Admin or user with no specific location assignments
        if (locations.length > 1) {
          newLocationId = 'all';
        } else {
          newLocationId = locations[0]?.id?.toString() || '';
        }
      } else if (userLocations.length === 1) {
        // User has exactly one location assigned
        newLocationId = userLocations[0]?.id?.toString() || '';
      } else if (userLocations.length > 1) {
        // User has multiple locations assigned
        newLocationId = 'all';
      }
      
      if (newLocationId) {
        console.log('Auto-selected location:', newLocationId);
        dispatch(setSelectedLocationId(newLocationId));
      }
    }
  }, [locations, selectedLocationId, locationsQueryFetching, company, mainCompany, mainUser, dispatch, canAccessAllLocations]);

  // Storage is written only at the point of explicit user selection — see
  // `setLocation` in `src/hooks/useLocations.js`. There is deliberately no
  // "mirror the live selection to storage" effect here: that used to persist
  // *every* selectedLocationId, including auto-selected defaults, which let a
  // bad auto-selection (e.g. a momentarily incomplete `locations` array)
  // silently overwrite a real, valid stored choice.

  return <>{children}</>;
};

export default LocationProvider;
